import React from 'react';
import {
    Chart as ChartJS
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ChartDataLabels);

const capitalize = str =>
    typeof str === 'string' && str.length > 0
        ? str.charAt(0).toUpperCase() + str.slice(1)
        : '';

// Fixed color palette
const colorGroups = {
    blues: [
        "rgb(6, 72, 135)",
        "rgb(8, 99, 187)",
        "rgb(25, 119, 210)",
        "rgb(102, 164, 224)",
        "rgb(62, 140, 216)",
    ],
    purples: [
        "rgb(56, 8, 142)",
        "rgb(75, 11, 193)",
        "rgb(95, 30, 214)",
        "rgb(149, 107, 227)",
        "rgb(121, 67, 219)",
    ],
    oranges: [
        "rgb(208, 123, 0)",
        "rgb(255, 151, 0)",
        "rgb(255, 156, 12)",
        "rgb(255, 194, 105)",
        "rgb(255, 175, 59)",
    ],
    yellows: [
        "rgb(208, 183, 0)",
        "rgb(255, 223, 0)",
        "rgb(255, 225, 12)",
        "rgb(255, 237, 105)",
        "rgb(255, 231, 59)",
    ]
};

const colorGroupKeys = Object.keys(colorGroups); // ["blues", "purples", ...]


const UserNeedScores = ({ scores }) => {
    const intl = useIntl();
    const theme = useTheme();

    if (!scores || Object.keys(scores).length === 0) {
        return null;
    }

    // Step 1: keys
    const topLevelKeys = Object.keys(scores);
    const allSubKeys = Array.from(
        new Set(topLevelKeys.flatMap(topKey => Object.keys(scores[topKey])))
    );

    // Step 2: assign colors for each top-level group
    const colorAssignment = {}; // subKey -> color
    const subKeySeen = new Set();

    topLevelKeys.forEach((topKey, groupIndex) => {
        const groupName = colorGroupKeys[groupIndex % colorGroupKeys.length];
        const shades = colorGroups[groupName];

        let shadeIndex = 0;
        Object.keys(scores[topKey]).forEach(subKey => {
            if (!subKeySeen.has(subKey)) {
                subKeySeen.add(subKey);
                colorAssignment[subKey] = shades[shadeIndex % shades.length];
                shadeIndex += 1;
            }
        });
    });

    // Step 3: build datasets per subKey
    const datasets = allSubKeys.map(subKey => ({
        label: capitalize(subKey),
        data: topLevelKeys.map(topKey => scores[topKey][subKey] || 0),
        backgroundColor: colorAssignment[subKey],
    }));

    // Step 4: chart data
    const labels = topLevelKeys.map(capitalize);
    const chartData = {
        labels,
        datasets,
    };

    // Step 5: options with legend and stacked layout
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'right',
                align: 'start',
            },
            datalabels: {
                display: true,
                color: '#fff',
                formatter: (value) => value >= 5 ? `${value}%` : '',
                anchor: 'center',
                align: 'center',
                font: {
                    weight: 'bold',
                },
                clamp: true, // prevents labels from overflowing
            },
            /*title: {
                display: true,
                text: intl.formatMessage({ id: "article.userneed.scoring.chart.title" }),
            },*/
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
            },
            y: {
                stacked: true,
                grid: { display: false },
                ticks: {
                    callback: value => `${value} %`,
                },
                min: 0,
                max: 100,
            },
        },
    };

    return <Bar data={chartData} options={chartOptions} />;
};

export default UserNeedScores;
